import { HHStack } from '@hinge-health/react-component-library';
import { Chip, Paper, Radio, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { KeyboardEvent, ReactNode } from 'react';
import {
  MOTION_DURATION,
  MOTION_EASING,
  usePrefersReducedMotion,
} from '../motion/motion';

interface SelectableCardProps {
  selected: boolean;
  onClick: () => void;
  recommended?: boolean;
  recommendedLabel?: string;
  disabled?: boolean;
  children: ReactNode;
}

export const SelectableCard = ({
  selected,
  onClick,
  recommended,
  recommendedLabel = 'Recommended',
  disabled,
  children,
}: SelectableCardProps): JSX.Element => {
  const theme = useTheme();
  const reduced = usePrefersReducedMotion();

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Paper
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      variant="outlined"
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      sx={{
        // Inner geometry is constant (`p: 2`, `borderWidth: 1`) so the radio
        // and content keep the same x-position across selected and unselected
        // states — radios stay aligned in a clean vertical column down the
        // list. The "selected" emphasis comes from a 1px ring drawn *outside*
        // the border via box-shadow, which doesn't take layout space and
        // therefore can't shift inner content.
        p: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        borderColor: selected
          ? theme.palette.primary.main
          : theme.palette.divider,
        borderWidth: 1,
        boxShadow: selected
          ? `0 0 0 1px ${theme.palette.primary.main}`
          : 'none',
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.06)
          : 'transparent',
        transition: reduced
          ? 'none'
          : `border-color ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}, background-color ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}, box-shadow ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}, transform ${MOTION_DURATION.feedback}ms ${MOTION_EASING.standard}`,
        '&:hover': disabled
          ? {}
          : {
              borderColor: theme.palette.primary.main,
              backgroundColor: selected
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.primary.main, 0.03),
            },
        // A faint dip on press. Returns to scale(1), so the constant inner
        // geometry (and the aligned radio column) is preserved at rest.
        '&:active': disabled || reduced ? {} : { transform: 'scale(0.99)' },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <HHStack direction="row" spacing={1.5} alignItems="flex-start">
        <Radio
          checked={selected}
          tabIndex={-1}
          disabled={disabled}
          // The Paper itself is the focus target; the inner Radio is
          // decorative and shouldn't grab keyboard focus on its own.
          sx={{ p: 0, mt: '2px', pointerEvents: 'none' }}
        />
        <HHStack flex={1} spacing={0.5} sx={{ minWidth: 0 }}>
          {children}
        </HHStack>
        {recommended && (
          <Chip
            size="small"
            color="success"
            label={recommendedLabel}
            sx={{ flexShrink: 0 }}
          />
        )}
      </HHStack>
    </Paper>
  );
};
